'use client';

import { Children, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/tailwind';
import type { UiResponsiveListProps } from './UiResponsiveList.types';
import UiGradientOverlay from '../UiGradientOverlay';

function UiResponsiveList({
    children,
    className,
    scrollClassName,
    listClassName,
    itemClassName,
    fadeEdges = true,
}: UiResponsiveListProps) {
    const items = useMemo(() => Children.toArray(children), [children]);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const overlayBase = 'md:hidden transition-opacity duration-300 ease-out pointer-events-none';

    useEffect(() => {
        if (!fadeEdges) return;

        const el = scrollRef.current;
        if (!el) return;

        let raf = 0;

        const update = () => {
            const t = 1;

            const left = el.scrollLeft > t;
            const right = el.scrollLeft + el.clientWidth < el.scrollWidth - t;

            setCanScrollLeft(left);
            setCanScrollRight(right);
        };

        const scheduleUpdate = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        };

        update();

        el.addEventListener('scroll', scheduleUpdate, { passive: true });

        const ro = new ResizeObserver(scheduleUpdate);
        ro.observe(el);

        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener('scroll', scheduleUpdate);
            ro.disconnect();
        };
    }, [fadeEdges, items.length]);

    return (
        <div className={cn('relative', className)}>
            <div
                ref={scrollRef}
                className={cn('overflow-x-auto snap-x snap-mandatory scrollbar-hidden px-4 md:px-0', scrollClassName)}
            >
                <ul className={cn('inline-flex gap-6 md:px-0 md:grid md:w-full', listClassName)}>
                    {items.map((child, i) => {
                        const key = isValidElement(child) && child.key != null ? String(child.key) : String(i);

                        return (
                            <li key={key} className={cn('snap-center shrink-0 w-[72vw] md:w-auto', itemClassName)}>
                                {child}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {fadeEdges && (
                <>
                    <UiGradientOverlay
                        variant="left"
                        className={cn(overlayBase, canScrollLeft ? 'opacity-100' : 'opacity-0')}
                    />

                    <UiGradientOverlay
                        variant="right"
                        className={cn(overlayBase, canScrollRight ? 'opacity-100' : 'opacity-0')}
                    />
                </>
            )}
        </div>
    );
}

export default UiResponsiveList;
